import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { ProjectAndCustomer } from '../project/projectType';
import ProjectApi from '../project/projectApi';
import { Upload, FolderUp, PictureInPicture2 } from 'lucide-react';
import { formatAddress } from '../lib/global_function';
import PhotoApi from '../photo/photoApi';
import { Button } from '../components/ui/button';
import { Select } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Alert } from '../components/ui/alert';

const UploadPhotoPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Photo Gallery', path: '/main/project/photo' },
    { label: 'Upload Photo', path: '/main/project/photo/upload' },
  ];

  const MAX_PHOTO_UPLOADED_PER_PROJECT = 15;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<ProjectAndCustomer | null>(null);
  const [projectsAndCustomers, setProjectsAndCustomers] = useState<ProjectAndCustomer[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [imageProjectTag, setImageProjectTag] = useState<'before_project' | 'after_project' | ''>('');
  const [fileError, setFileError] = useState<string>('');
  const [photoCount, setPhotoCount] = useState<number>(0);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTagChange = (tag: 'before_project' | 'after_project') => {
    setImageProjectTag(tag);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    updatePhotoCount();
  }, [selectedProjectId]);

  useEffect(() => {
    const fetchProjectsAndCustomers = async () => {
      try {
        const fetchedProjectsAndCustomers = await ProjectApi.getProjectsAndCustomers();
        setProjectsAndCustomers(fetchedProjectsAndCustomers);

        const projectFromState = location.state?.projectAndCustomer;
        if (projectFromState) {
          setSelectedProjectId(projectFromState.project_id.toString());
          setSelectedCustomer(projectFromState);
        }
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    };
    fetchProjectsAndCustomers();
  }, [location.state]);

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
    const selected = projectsAndCustomers.find(
      (projCust) => projCust.project_id.toString() === projectId.toString()
    );
    setSelectedCustomer(selected || null);
  };

  const updatePhotoCount = async () => {
    if (selectedProjectId) {
      try {
        const count = await PhotoApi.getPhotoCount('project', selectedProjectId);
        setPhotoCount(count);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    }
  };


  const handleViewPhoto = () => {
    navigate('/main/project/photo', {
      state: { selectedProject: selectedCustomer },
      replace: false
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];

      if (selectedFile.size > 10 * 1024 * 1024) {
        setFileError('File size must be less than 10MB');
        setFile(null);
        return;
      }

      setFileError('');
      setFile(selectedFile);
    }
  };


  const handleUpload = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setErrorMessage(null);
    setMessage(null);

    if (!selectedProjectId) {
      setErrorMessage('Please select a project');
      return;
    }
    if (!imageProjectTag) {
      setErrorMessage('Please select whether the photo was taken before or after the project');
      return;
    }
    if (!file) {
      setErrorMessage('Please select a file to upload');
      return;
    }

    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validImageTypes.includes(file.type)) {
      setErrorMessage('Please select a valid image file (JPEG, PNG, GIF, or WEBP)');
      return;
    }

    try {
      setLoading(true);
      const currentCount = await PhotoApi.getPhotoCount('project', selectedProjectId);
      if (currentCount >= MAX_PHOTO_UPLOADED_PER_PROJECT) {
        setErrorMessage(`Maximum number of photos (${MAX_PHOTO_UPLOADED_PER_PROJECT}) has been reached for this project`);
        return;
      }

      await PhotoApi.upload(file, {
        project_id: parseInt(selectedProjectId),
        image_project_tag: imageProjectTag,
        image_for_tag: 'project',
        image_size_tag: 'all'
      });

      // Navigate back to photo page after successful upload
      navigate('/main/project/photo', {
        state: { selectedProject: selectedCustomer },
        replace: false  // Don't replace history, allow back button
      });

    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Error uploading photo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-bold text-foreground mt-6 mb-4">Upload Photo</h2>

        {errorMessage && (
          <Alert variant="error">
            {errorMessage}
          </Alert>
        )}

        {message && (
          <Alert variant="success">
            {message} You can upload another photo.
          </Alert>
        )}

        {selectedCustomer && (
          <div className="my-4">
            <Label className="text-base">
              <strong>Customer:</strong> {selectedCustomer.customer_first_name} {selectedCustomer.customer_last_name},{' '}
              {formatAddress(
                selectedCustomer.customer_address_1,
                '',
                selectedCustomer.customer_city,
                selectedCustomer.customer_state,
                selectedCustomer.customer_zip
              )}
              , {selectedCustomer.customer_email}, {selectedCustomer.customer_phone}
            </Label>
          </div>
        )}

        <div className="mb-4">
          <Label htmlFor="project-select" className="text-base">
            Select Project: <span className="text-destructive">*</span>
          </Label>
          <Select
            id="project-select"
            name="selectedProject"
            value={selectedProjectId || ''}
            onChange={(e) => handleProjectChange(e.target.value)}
            required
            className="mt-2"
          >
            <option value="">Select Project</option>
            {projectsAndCustomers.map((projCust) => (
              <option key={projCust.project_id} value={projCust.project_id}>
                {projCust.name}
                {projCust.project_description
                  ? ` - ${projCust.project_description.substring(0, 100)}`
                  : ''}
              </option>
            ))}
          </Select>
        </div>

        <fieldset className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
          <legend className="text-lg font-semibold text-foreground px-2">Upload Photo:</legend>

          <div className="mb-5 flex items-center justify-between gap-4">
            <span className="text-base font-medium text-foreground">
              Photos uploaded: {photoCount} of {MAX_PHOTO_UPLOADED_PER_PROJECT} allowed per project
            </span>
            {photoCount > 0 && (
              <Button onClick={handleViewPhoto} size="medium" variant="default">
                <PictureInPicture2 className="size-5" />
                View Photo
              </Button>
            )}
          </div>
          <div className="mb-2.5">
            <Label className="text-base">
              Select the button below for photo taken Before Project or After Project?
            </Label>

          </div>

          <div className="mb-5 flex gap-5">
            <label className="flex items-center gap-2 text-base font-medium text-foreground cursor-pointer">
              <input
                type="radio"
                name="imageTag"
                checked={imageProjectTag === 'before_project'}
                onChange={() => handleTagChange('before_project')}
                className="size-4 cursor-pointer"
              />
              Before Project
            </label>
            <label className="flex items-center gap-2 text-base font-medium text-foreground cursor-pointer">
              <input
                type="radio"
                name="imageTag"
                checked={imageProjectTag === 'after_project'}
                onChange={() => handleTagChange('after_project')}
                className="size-4 cursor-pointer"
              />
              After Project
            </label>
          </div>

          <div>
            <div className="flex gap-2.5 mb-2.5 items-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <Button
                onClick={handleBrowseClick}
                size="medium"
                variant="inverted"
                type="button"
              >
                <FolderUp className="size-5" />
                Browse
              </Button>
              {file && <span className="text-base font-medium text-foreground">{file.name}</span>}
            </div>
            {fileError && (
              <Alert variant="error" className="mb-2.5">
                {fileError}
              </Alert>
            )}

            <div className="mt-2.5">
              <Button
                onClick={handleUpload}
                disabled={loading}
                size="medium"
                variant="default"
                type="button"
              >
                <Upload className="size-5" />
                {loading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </fieldset>

        <div className="mb-[150px]"></div>
      </div>
    </>
  );
};

export default UploadPhotoPage;